import { inject, singleton } from "tsyringe";
import {
  CookieStorage,
  FandomContextGroups,
  ContextRepository,
  TopLevelDomainExtractor,
  WikiConfig,
} from "@identity/utils";
import { BeaconGenerator } from "../BeaconGenerator";
import { PageViewNumberGenerator } from "../PageViewNumberGenerator";
import { UUID, WINDOW } from "@identity/symbols";
import {
  CommunicationService,
  eventsRepository,
} from "../CommunicationService";

const MS_30_MIN = 1000 * 60 * 30;
const MS_180_DAYS = 1000 * 60 * 60 * 24 * 180;
const MS_730_DAYS = 1000 * 60 * 60 * 24 * 730;

@singleton()
export class TrackingContextSetupService {
  constructor(
    @inject(ContextRepository) private contextRepository: ContextRepository,
    @inject(CookieStorage) private cookieRepository: CookieStorage,
    @inject(UUID) private uuid: () => string,
    @inject(BeaconGenerator) private beaconGenerator: BeaconGenerator,
    @inject(PageViewNumberGenerator)
    private pageViewNumberGenerator: PageViewNumberGenerator,
    @inject(WINDOW)
    private readonly window: Window,
    @inject(WikiConfig)
    private readonly wikiConfig: WikiConfig,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
  ) {
    this.communicationService.on(eventsRepository.PAGE_CHANGED, () => {
      this.updatePageViewData();
    });
  }

  private set<T extends keyof FandomContext[FandomContextGroups.tracking]>(
    name: T,
    trackingProperty: CookieProperty<
      FandomContext[FandomContextGroups.tracking][T]
    >,
  ) {
    this.contextRepository.set(
      FandomContextGroups.tracking,
      name,
      trackingProperty,
    );
    this.cookieRepository.set(name, trackingProperty);
  }

  private remove<T extends keyof FandomContext[FandomContextGroups.tracking]>(
    name: T,
  ) {
    this.contextRepository.remove(FandomContextGroups.tracking, name);
    this.cookieRepository.remove(name);
  }

  private updatePageViewData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    this.contextRepository.set(FandomContextGroups.tracking, "pvUID", {
      domain: domain,
      expiresMS: MS_30_MIN,
      value:
        this.wikiConfig.get("pvUID") || // UCP
        this.window.pvUID || // F2
        this.uuid(),
    });

    this.set("pvNumber", {
      domain: domain,
      expiresMS: MS_30_MIN,
      value: this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumber"),
    });

    this.set("pvNumberGlobal", {
      domain: topLevelDomain,
      expiresMS: MS_30_MIN,
      value:
        this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumberGlobal"),
    });
  }

  private updateUserTrackingData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    const beaconId =
      this.cookieRepository.get("wikia_beacon_id") ||
      this.cookieRepository.get("beaconId");
    const beaconParams = {
      domain: topLevelDomain,
      expiresMS: MS_180_DAYS,
      value: beaconId || this.beaconGenerator.generate(),
    };
    this.contextRepository.set(
      FandomContextGroups.tracking,
      "beaconId",
      beaconParams,
    );
    this.cookieRepository.set("wikia_beacon_id", beaconParams);

    const b2 = this.cookieRepository.get("_b2");
    const b2Params = {
      domain: topLevelDomain,
      expiresMS: MS_730_DAYS,
      value: b2 || this.beaconGenerator.generateV2(),
    };
    this.cookieRepository.set("_b2", b2Params);

    const mwSessionId = this.wikiConfig.get("sessionId", this.window.sessionId);
    const sessionId = mwSessionId || this.cookieRepository.get("sessionId");
    const sessionParams = {
      domain: topLevelDomain,
      expiresMS: MS_30_MIN,
      value: sessionId || this.uuid(),
    };
    this.set("sessionId", sessionParams);

    // This cookie was set by TrackingOptIn for 180 days with a beacon-like value.
    const wikiaSessionId = this.cookieRepository.get("wikia_session_id");
    this.cookieRepository.set("wikia_session_id", {
      domain: topLevelDomain,
      expiresMS: MS_180_DAYS,
      value: wikiaSessionId || this.beaconGenerator.generate(),
    });
  }

  public setup(): void {
    this.updateUserTrackingData();
    this.updatePageViewData();
  }

  public clear(): void {
    this.remove("beaconId");
    this.remove("sessionId");
    this.remove("pvNumber");
    this.remove("pvNumberGlobal");
  }
}
